import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  CircularProgress,
  Switch,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useApp } from '../../../context/AppContext';
import api from '../../../utils/api';

const TimeSlotModal = ({ open, onClose, onSave, slot }) => {
  const { actions } = useApp();
  const [formData, setFormData] = useState({
    startTime: null,
    endTime: null,
    branches: [],
    active: true,
    maxOrders: {}, // Store max orders per branch
    disabledPods: [] // Only used in edit mode
  });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [pods, setPods] = useState([]);
  const [podsLoading, setPodsLoading] = useState(false);
  const [errors, setErrors] = useState({});


  useEffect(() => {
    if (open) {
      fetchBranches();
      if (slot) {
        fetchPods();
        // Edit mode - populate form with existing data
        setFormData({
          startTime: slot.startTime ? parseTimeString(slot.startTime) : null,
          endTime: slot.endTime ? parseTimeString(slot.endTime) : null,
          branches: slot.branches || [],
          active: slot.active !== false,
          maxOrders: slot.maxOrders || {}, // Populate maxOrders
          disabledPods: Array.isArray(slot.disabledPods) ? slot.disabledPods : []
        });
      } else {
        // Add mode - reset form
        setFormData({
          startTime: null,
          endTime: null,
          branches: [],
          active: true,
          maxOrders: {},
          disabledPods: []
        });
      }
      setErrors({});
    }
  }, [open, slot]);

  const parseTimeString = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return date;
  };

  const formatTimeForSubmit = (date) => {
    if (!date) return '';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const fetchBranches = async () => {
    try {
      setBranchesLoading(true);
      const response = await api.get('/stores');
      
      // Extract branches from all stores
      let allBranches = [];
      if (response.data && response.data.stores) {
        response.data.stores.forEach(store => {
          // Each store is treated as a branch
          allBranches.push({
            id: store.id,
            name: store.storeName,
            branchId: store.branchId,
            storeName: store.storeName,
            storeId: store.id,
            location: store.location,
            address: store.address
          });
        });
      }
      
      setBranches(allBranches);
    } catch (error) {
      console.error('Error fetching branches:', error);
      actions.showSnackbar('Failed to fetch branches', 'error');
    } finally {
      setBranchesLoading(false);
    }
  };

  const fetchPods = async () => {
    try {
      setPodsLoading(true);
      const response = await api.get('/pods/all', { params: { activeOnly: 'true' } });
      setPods(response.data?.pods || []);
    } catch (error) {
      console.error('Error fetching pods:', error);
      actions.showSnackbar('Failed to fetch pods', 'error');
    } finally {
      setPodsLoading(false);
    }
  };



  const validateForm = () => {
    const newErrors = {};

    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }

    if (formData.startTime && formData.endTime) {
      if (formData.startTime >= formData.endTime) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    if (formData.branches.length === 0) {
      newErrors.branches = 'Please select at least one branch';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      const submitData = {
        startTime: formatTimeForSubmit(formData.startTime),
        endTime: formatTimeForSubmit(formData.endTime),
        branches: formData.branches,
        active: formData.active,
        maxOrders: formData.maxOrders // Include maxOrders in submission
      };

      if (slot) {
        // Edit existing slot
        await api.put(`/time-slots/${slot.id}`, {
          ...submitData,
          disabledPods: Array.isArray(formData.disabledPods) ? formData.disabledPods : []
        });
        actions.showSnackbar('Time slot updated successfully', 'success');
      } else {
        // Create new slot
        await api.post('/time-slots', submitData);
        actions.showSnackbar('Time slot created successfully', 'success');
      }

      onSave();
    } catch (error) {
      console.error('Error saving time slot:', error);
      actions.showSnackbar(
        error.response?.data?.message || 'Failed to save time slot', 
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBranchToggle = (branch) => {
    setFormData(prev => {
      const isSelected = prev.branches.some(b => b.id === branch.id);
      
      if (isSelected) {
        // Remove branch and its max orders
        const newMaxOrders = { ...prev.maxOrders };
        delete newMaxOrders[branch.id];
        
        return {
          ...prev,
          branches: prev.branches.filter(b => b.id !== branch.id),
          maxOrders: newMaxOrders
        };
      } else {
        // Add branch without default max orders
        return {
          ...prev,
          branches: [...prev.branches, branch],
          maxOrders: {
            ...prev.maxOrders,
            [branch.id]: prev.maxOrders[branch.id] || ''
          }
        };
      }
    });
  };

  const isBranchSelected = (branch) => {
    return formData.branches.some(b => b.id === branch.id);
  };

  const getCurrentOrdersForBranch = (branchId) => {
    if (!slot) return { today: 0, tomorrow: 0, max: 0 };
    
    const branch = slot.branches?.find(b => b.id === branchId);
    if (!branch) return { today: 0, tomorrow: 0, max: 0 };
    
    return {
      today: branch.currentOrdersToday || 0,
      tomorrow: branch.currentOrdersTomorrow || 0,
      max: branch.maxOrders || 0
    };
  };

  const handleSelectAll = () => {
    if (formData.branches.length === branches.length) {
      // Deselect all - clear branches and maxOrders
      setFormData(prev => ({ 
        ...prev, 
        branches: [],
        maxOrders: {}
      }));
    } else {
      // Select all - add all branches without default maxOrders
      const newMaxOrders = {};
      branches.forEach(branch => {
        newMaxOrders[branch.id] = formData.maxOrders[branch.id] || '';
      });
      
      setFormData(prev => ({ 
        ...prev, 
        branches: [...branches],
        maxOrders: newMaxOrders
      }));
    }
  };

  const handleMaxOrdersChange = (branchId, value) => {
    setFormData(prev => ({
      ...prev,
      maxOrders: {
        ...prev.maxOrders,
        [branchId]: parseInt(value) || 0
      }
    }));
  };

  const getPodLabel = (pod) => {
    const storeName = pod.store?.storeName || pod.storeName || '';
    const podName = pod.name || pod.podName || pod.id;
    return storeName ? `${storeName} - ${podName}` : podName;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" fontWeight="bold">
            {slot ? 'Edit Time Slot' : 'Add New Time Slot'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Current Orders Summary - Only show in edit mode */}
            {slot && (
              <Box sx={{ 
                bgcolor: 'primary.light', 
                p: 2, 
                borderRadius: 1,
                border: 1,
                borderColor: 'primary.main'
              }}>
                <Typography variant="h6" gutterBottom color="primary.dark">
                  📊 Current Orders Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Branches: {slot.branches?.length || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Today: {slot.branches?.reduce((sum, branch) => sum + (branch.currentOrdersToday || 0), 0) || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                     Tomorrow: {slot.branches?.reduce((sum, branch) => sum + (branch.currentOrdersTomorrow || 0), 0) || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Max Orders: {slot.branches?.reduce((sum, branch) => sum + (branch.maxOrders || 0), 0) || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Time Selection */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Time Range
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TimePicker
                    label="Start Time"
                    value={formData.startTime}
                    onChange={(newValue) => {
                      setFormData(prev => ({ ...prev, startTime: newValue }));
                      if (errors.startTime) {
                        setErrors(prev => ({ ...prev, startTime: null }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!errors.startTime}
                        helperText={errors.startTime}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TimePicker
                    label="End Time"
                    value={formData.endTime}
                    onChange={(newValue) => {
                      setFormData(prev => ({ ...prev, endTime: newValue }));
                      if (errors.endTime) {
                        setErrors(prev => ({ ...prev, endTime: null }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!errors.endTime}
                        helperText={errors.endTime}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Branch Selection */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Applicable Branches
                </Typography>
                {branches.length > 0 && (
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    color="primary"
                  >
                    {formData.branches.length === branches.length ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </Box>

              {errors.branches && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.branches}
                </Alert>
              )}

              {branchesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : branches.length === 0 ? (
                <Alert severity="info">
                  No branches found. Please add stores with branches first.
                </Alert>
              ) : (
                <Box sx={{ overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1,
                    '@media (max-width: 600px)': {
                      gridTemplateColumns: '1fr'
                    }
                  }}>
                    {branches.map((branch) => (
                      <Box key={branch.id} sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 1,
                        border: 1,
                        borderColor: isBranchSelected(branch) ? 
                          (() => {
                            const orders = getCurrentOrdersForBranch(branch.id);
                            const totalCurrent = orders.today + orders.tomorrow;
                            const max = orders.max;
                            if (max === 0) return 'divider';
                            if (totalCurrent >= max) return 'error.main';
                            if (totalCurrent >= max * 0.8) return 'warning.main';
                            return 'divider';
                          })() : 'divider',
                        borderRadius: 1,
                        bgcolor: isBranchSelected(branch) ?  'transparent' : 'action.selected'
                      }}>
                        <FormControlLabel
                          sx={{ flex: 1, m: 0 }}
                          control={
                            <Checkbox
                              checked={isBranchSelected(branch)}
                              onChange={() => handleBranchToggle(branch)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" fontWeight="medium">
                              {branch.name}
                            </Typography>
                          }
                        />
                        {isBranchSelected(branch) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                               Today:
                              </Typography>
                              <Typography variant="caption" fontWeight="bold" color="primary">
                                {getCurrentOrdersForBranch(branch.id).today}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                 Tomorrow:
                              </Typography>
                              <Typography variant="caption" fontWeight="bold" color="primary">
                                {getCurrentOrdersForBranch(branch.id).tomorrow}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Max Orders:
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={formData.maxOrders[branch.id] || ''}
                                onChange={(e) => handleMaxOrdersChange(branch.id, e.target.value)}
                                placeholder="Max"
                                inputProps={{ 
                                  min: 1, 
                                  max: 999,
                                  style: { textAlign: 'center' }
                                }}
                                sx={{ 
                                  width: 60,
                                  '& .MuiOutlinedInput-root': {
                                    height: 32
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {formData.branches.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {formData.branches.length} branch{formData.branches.length !== 1 ? 'es' : ''} selected
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Disabled Pods - Only show in edit mode */}
            {slot && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Disabled Pods
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select pods for which this time slot should be disabled.
                </Typography>

                <FormControl fullWidth disabled={podsLoading}>
                  <InputLabel id="disabled-pods-label">
                    {podsLoading ? 'Loading pods...' : 'Select pods'}
                  </InputLabel>
                  <Select
                    labelId="disabled-pods-label"
                    multiple
                    value={formData.disabledPods}
                    label={podsLoading ? 'Loading pods...' : 'Select pods'}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        disabledPods: typeof value === 'string' ? value.split(',') : value
                      }));
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((podId) => {
                          const pod = pods.find((p) => p.id === podId);
                          return (
                            <Chip
                              key={podId}
                              label={pod ? getPodLabel(pod) : podId}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {pods.map((pod) => (
                      <MenuItem key={pod.id} value={pod.id}>
                        {getPodLabel(pod)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Divider />

            {/* Status */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      Active
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.active ? 'This time slot is available for ordering' : 'This time slot is disabled'}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              slot ? 'Update' : 'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TimeSlotModal; 